import React from "react";
import { salesInvoiceData } from "../data/salesData";

const SalesTemplate = () => {
  const data = salesInvoiceData || {};

  // Destructure all fields from data
  const {
    company = {},
    invoiceNumber,
    date,
    customer = {},
    salesperson,
    poNumber,
    shipDate,
    shipVia,
    fob,
    terms,
    items = [],
    taxRate = 0,
    sh = 0,
    other = 0,
  } = data;

  // Auto calculate subtotal, tax, total
  const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const tax = (subtotal * taxRate) / 100;
  const total = subtotal + tax + Number(sh) + Number(other);

  const primaryBlue = "bg-[#4F6D95]";

  return (
    <div id="sales-invoice" className="bg-white p-6 text-gray-800 font-sans mx-auto">
      {/* Header */}
      <div className="flex justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#4F6D95] mb-1">{company.name}</h1>
          <div className="text-xs text-gray-500 leading-tight">
            {company.address}<br />
            {company.city}, {company.state} {company.zip}<br />
            Phone: {company.phone}<br />
            Fax: {company.fax}<br />
            Email: {company.email}<br />
            Website: {company.website}
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-4xl font-light text-[#A4BADC] uppercase mb-1">Invoice</h2>
          <div className="text-xs grid grid-cols-2 border border-gray-300">
            <span className="border-r border-b p-1 font-bold bg-gray-50 uppercase">Date</span>
            <span className="border-b p-1 text-center">{date}</span>
            <span className="border-r border-b p-1 font-bold bg-gray-50 uppercase">Invoice #</span>
            <span className="border-b p-1 text-center">{invoiceNumber}</span>
            <span className="border-r p-1 font-bold bg-gray-50 uppercase">Customer ID</span>
            <span className="p-1 text-center">{customer.id}</span>
          </div>
        </div>
      </div>

      {/* Bill To / Ship To */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
        <div>
          <h3 className={`${primaryBlue} text-white px-2 py-0.5 font-bold uppercase`}>Bill To:</h3>
          <div className="p-2 min-h-[60px] border border-t-0 border-gray-300">
            <p>{salesperson}</p>
            <p>{shipDate}</p>
            <p>{customer.address}</p>
            <p>{customer.shipVia}, {customer.fob} {customer.zip}</p>
            <p>{customer.phone}</p>
          </div>
        </div>
        <div>
          <h3 className={`${primaryBlue} text-white px-2 py-0.5 font-bold uppercase`}>Ship To:</h3>
          <div className="p-2 min-h-[60px] border border-t-0 border-gray-300">
            <p>{customer.name}</p>
            <p>{customer.companyName}</p>
            <p>{customer.address}</p>
            <p>{customer.city}, {customer.state} {customer.zip}</p>
            <p>{customer.phone}</p>
          </div>
        </div>
      </div>

      {/* Sales Info */}
      <table className="w-full border-collapse mb-4 text-[10px] text-center uppercase font-bold">
        <thead>
          <tr className={`${primaryBlue} text-white`}>
            <th className="border border-white/20 py-1">Salesperson</th>
            <th className="border border-white/20 py-1">P.O. #</th>
            <th className="border border-white/20 py-1">Ship Date</th>
            <th className="border border-white/20 py-1">Ship Via</th>
            <th className="border border-white/20 py-1">F.O.B.</th>
            <th className="border border-white/20 py-1">Terms</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 py-1">{salesperson}</td>
            <td className="border border-gray-300 py-1">{poNumber}</td>
            <td className="border border-gray-300 py-1">{shipDate}</td>
            <td className="border border-gray-300 py-1">{shipVia}</td>
            <td className="border border-gray-300 py-1">{fob}</td>
            <td className="border border-gray-300 py-1">{terms}</td>
          </tr>
        </tbody>
      </table>

      {/* Items Table */}
      <table className="w-full border-collapse mb-4 text-xs">
        <thead>
          <tr className={`${primaryBlue} text-white font-bold uppercase`}>
            <th className="border border-white/20 py-1 w-12">Item #</th>
            <th className="border border-white/20 py-1 text-left px-2">Description</th>
            <th className="border border-white/20 py-1 w-16">Qty</th>
            <th className="border border-white/20 py-1 w-24">Unit Price (₹)</th>
            <th className="border border-white/20 py-1 w-24">Total (₹)</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} className="h-6">
              <td className="border border-gray-300 px-2 text-center">{item.id}</td>
              <td className="border border-gray-300 px-2">{item.desc}</td>
              <td className="border border-gray-300 px-2 text-center">{item.qty}</td>
              <td className="border border-gray-300 px-2 text-right">{item.rate}</td>
              <td className="border border-gray-300 px-2 text-right font-medium">{item.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end w-full text-xs">
        <table className="w-48">
          <tbody>
            <tr>
              <td className="border border-gray-300 px-2 py-1 font-bold bg-gray-50">Subtotal</td>
              <td className="border border-gray-300 px-2 text-right">₹ {subtotal.toFixed(2)}</td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-2 py-1 font-bold bg-gray-50">TAX ({taxRate}%)</td>
              <td className="border border-gray-300 px-2 text-right">₹ {tax.toFixed(2)}</td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-2 py-1 font-bold bg-gray-50">S & H</td>
              <td className="border border-gray-300 px-2 text-right">₹ {sh.toFixed(2)}</td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-2 py-1 font-bold bg-gray-50">Other</td>
              <td className="border border-gray-300 px-2 text-right">₹ {other.toFixed(2)}</td>
            </tr>
            <tr className="text-sm font-bold">
              <td className={`${primaryBlue} text-white px-2 py-1`}>TOTAL</td>
              <td className="border border-gray-300 px-2 text-right bg-blue-50">₹ {total.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="text-center mt-8 text-[10px] space-y-1">
        <p className="font-bold italic">Make all checks payable to {company.name}</p>
        <p>If you have any questions about this invoice, please contact {company.email}</p>
        <p className="text-sm font-bold italic text-blue-900 mt-2">Thank You For Your Business!</p>
      </div>
    </div>
  );
};

export default SalesTemplate;
